"use client";

import Link from "next/link";
import { ArrowRight, CircleHelp, Crown, Minus, Plus, ScrollText } from "lucide-react";
import { useTranslations } from "next-intl";

import { formatNumber } from "@/lib/format-datetime";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

type QuestionKey = "unrated" | "firstRated" | "staff" | "freeze" | "integrity";

/**
 * The questions this contest can answer truthfully (`EX-PUB20`).
 *
 * The block used to ask every question on every contest, so an unrated,
 * points-scored contest with no freeze explained the rated round's first move
 * and the scoreboard freeze it does not have. What "unrated" means, whether
 * staff compete and how the contest is kept honest apply everywhere; the rated
 * question only where a rating moves, the freeze only where there is one.
 */
export function faqQuestions(contest: { rated: boolean; hasFreeze: boolean }): QuestionKey[] {
  const keys: QuestionKey[] = ["unrated"];
  if (contest.rated) keys.push("firstRated");
  keys.push("staff");
  if (contest.hasFreeze) keys.push("freeze");
  keys.push("integrity");
  return keys;
}

/**
 * `EX-PUB22`: on a community contest the answers that said "round" say
 * "contest", as the heading already did (`EX-PUB20`). The question keys stay
 * the same; only the copy they read changes.
 */
const COMMUNITY_COPY: Partial<Record<QuestionKey, string>> = {
  staff: "staffCommunity",
  freeze: "freezeCommunity",
  integrity: "integrityCommunity",
};

/**
 * The first-time-contestant FAQ (`EX-RND02`).
 *
 * The heading asks about a Progsity round, not a *rated* one, because the
 * first warm-up round was unrated: a heading that named the rated case was
 * simply wrong on the page it sat on. `EX-PUB20` applied the same test to who
 * runs it: on a community contest (`official: false`) the heading asks about
 * a contest on Progsity, because calling a student's contest a Progsity round
 * is the mislabel `EX-PUB18` removed from the landing.
 *
 * Static, shared copy across every contest rather than per-round content, so
 * a native `<details>` accordion (the homepage FAQ's own pattern) is the right
 * weight here, not `CourseFaqSection`'s stateful, admin-authored one.
 *
 * `EX-CPG02` (Wave 30 decision 5): a full-width band at the foot of the
 * Overview. A dark panel holds the heading and the doors a newcomer looks for
 * next (how submitting works, the rules page, how the rating works on a rated
 * round); beside it the questions are numbered and the first one is open.
 *
 * `slug` and `locale` (`EX-RUL01`) exist only so the `integrity` answer can
 * link to `/contests/[slug]/rules` - every other answer stays contest-agnostic.
 */
export function ContestFirstTimerFaq({
  slug,
  locale,
  official,
  rated,
  hasFreeze,
  onOpenGuide,
}: {
  slug: string;
  locale: string;
  official: boolean;
  rated: boolean;
  hasFreeze: boolean;
  /** The "How submitting works" guide; the door is drawn only when the page can open it. */
  onOpenGuide?: () => void;
}) {
  const t = useTranslations("contest.faq");
  const rulesHref = `/${locale}/contests/${slug}/rules`;

  return (
    <Card data-testid="contest-faq" className="mt-8 flex flex-col overflow-hidden lg:flex-row">
      <div className="flex flex-col gap-4 bg-neutral-950 px-6 py-7 text-white sm:px-8 sm:py-9 lg:w-[380px] lg:shrink-0">
        <span aria-hidden className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-primary">
          <CircleHelp className="h-6 w-6" />
        </span>
        <h2 className="text-2xl font-bold leading-tight tracking-tight sm:text-[28px]">
          {t(official ? "heading" : "headingCommunity")}
        </h2>
        <p className="text-[15px] leading-relaxed text-neutral-300">{t("intro")}</p>
        <div className="mt-1 flex flex-col gap-2">
          {onOpenGuide ? (
            <Button type="button" variant="outlineDark" className="w-full justify-between" onClick={onOpenGuide}>
              <span className="inline-flex items-center gap-2.5">
                <CircleHelp className="h-[18px] w-[18px] text-accent-peach" aria-hidden />
                {t("linkSubmitting")}
              </span>
              <ArrowRight className="h-4 w-4 text-neutral-400" aria-hidden />
            </Button>
          ) : null}
          <Button asChild variant="outlineDark" className="w-full justify-between">
            <Link href={rulesHref}>
              <span className="inline-flex items-center gap-2.5">
                <ScrollText className="h-[18px] w-[18px] text-accent-peach" aria-hidden />
                {t("linkRules")}
              </span>
              <ArrowRight className="h-4 w-4 text-neutral-400" aria-hidden />
            </Link>
          </Button>
          {rated ? (
            <Button asChild variant="outlineDark" className="w-full justify-between">
              <Link href={`/${locale}/judge/leaderboard`}>
                <span className="inline-flex items-center gap-2.5">
                  <Crown className="h-[18px] w-[18px] text-accent-peach" aria-hidden />
                  {t("linkRating")}
                </span>
                <ArrowRight className="h-4 w-4 text-neutral-400" aria-hidden />
              </Link>
            </Button>
          ) : null}
        </div>
        <p className="text-[13.5px] leading-relaxed text-neutral-400">{t("askNote")}</p>
      </div>

      <div className="min-w-0 flex-1 px-5 py-3 sm:px-8 sm:py-4">
        {faqQuestions({ rated, hasFreeze }).map((key, index) => {
          const copy = official ? key : (COMMUNITY_COPY[key] ?? key);
          return (
            <details key={key} open={index === 0} className="group border-b border-neutral-100 last:border-b-0">
              <summary className="flex min-h-[68px] cursor-pointer list-none items-center gap-4 py-3.5 text-neutral-900 [&::-webkit-details-marker]:hidden">
                <span className="w-8 shrink-0 font-mono text-[15px] font-bold tabular-nums text-neutral-500 group-open:text-primary-ink">
                  {formatNumber(index + 1, locale).padStart(2, formatNumber(0, locale))}
                </span>
                <span className="flex-1 text-base font-bold leading-snug">{t(`${copy}.q`)}</span>
                <span
                  aria-hidden
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-900 transition-colors duration-200 group-open:bg-primary group-open:text-white"
                >
                  <Plus className="h-4 w-4 group-open:hidden" />
                  <Minus className="hidden h-4 w-4 group-open:block" />
                </span>
              </summary>
              <p className="pb-5 pl-12 pr-2 text-[15px] leading-relaxed text-neutral-700 sm:pr-12">
                {key === "integrity"
                  ? t.rich(`${copy}.a`, {
                      link: (chunks) => (
                        <Link href={rulesHref} className="font-medium text-primary-ink underline underline-offset-2">
                          {chunks}
                        </Link>
                      ),
                    })
                  : t(`${copy}.a`)}
              </p>
            </details>
          );
        })}
      </div>
    </Card>
  );
}
